import { useState, useEffect, useMemo } from "react";
import { SurvivalScoreSkeleton } from "@/components/SkeletonLoaders";
import { motion } from "framer-motion";
import {
  Lock, ArrowRight, Lightbulb, Loader2, TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/authContext";
import { useSubscription } from "@/lib/subscriptionContext";
import { useAppContext } from "@/lib/onboardingContext";
import { getSurvivalScoreMeta } from "@/lib/survivalScoreLabels";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

function calcScore(revenue: number, laborCost: number, foodCost: number, rent: number) {
  if (revenue <= 0) return 0;
  const lp = (laborCost / revenue) * 100;
  const fp = (foodCost / revenue) * 100;
  const rp = (rent / revenue) * 100;
  const ls = Math.max(0, Math.min(33, 33 * (1 - Math.max(0, lp - 30) / 30)));
  const fs = Math.max(0, Math.min(33, 33 * (1 - Math.max(0, fp - 32) / 30)));
  const rs = Math.max(0, Math.min(34, 34 * (1 - Math.max(0, rp - 10) / 20)));
  return Math.round(ls + fs + rs);
}

function scoreColor(s: number) {
  if (s >= 75) return { text: "text-emerald-500", bg: "bg-emerald-500", label: "Healthy" };
  if (s >= 50) return { text: "text-amber-500", bg: "bg-amber-500", label: "At Risk" };
  return { text: "text-red-500", bg: "bg-red-500", label: "Critical" };
}

interface ScoreEntry {
  month_label: string;
  score: number;
  revenue: number;
  labor_cost: number;
  food_cost: number;
  rent: number;
  recommendations: string[];
}

export default function SurvivalScoreCard() {
  const { user } = useAuth();
  const { subscription } = useSubscription();
  const { onboarding } = useAppContext();
  const meta = getSurvivalScoreMeta(onboarding?.businessType);
  const Icon = meta.icon;
  const plan = subscription?.plan ?? "free";
  // Survival Score is unlocked for Pro and above; Free sees a teaser preview.
  // Legacy 'starter' is collapsed to Free (deprecated tier).
  const PLAN_RANK: Record<string, number> = { free: 0, starter: 0, pro: 1, business: 2, enterprise: 3 };
  const isLocked = (PLAN_RANK[plan] ?? 0) < PLAN_RANK.pro;

  const [history, setHistory] = useState<ScoreEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [revenue, setRevenue] = useState("");
  const [laborCost, setLaborCost] = useState("");
  const [foodCost, setFoodCost] = useState("");
  const [rent, setRent] = useState("");
  const [recommendations, setRecommendations] = useState<string[]>([]);

  const currentMonth = new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" });

  useEffect(() => {
    if (!user || isLocked) { setLoading(false); return; }
    (async () => {
      const { data } = await supabase
        .from("survival_scores")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: true });
      if (data) {
        setHistory(
          data.map((d: any) => ({
            month_label: d.month_label,
            score: d.score,
            revenue: d.revenue,
            labor_cost: d.labor_cost,
            food_cost: d.food_cost,
            rent: d.rent,
            recommendations: (d.recommendations as string[]) ?? [],
          }))
        );
        // Pre-fill latest recommendations
        const latest = data[data.length - 1];
        if (latest) {
          setRecommendations((latest.recommendations as string[]) ?? []);
        }
      }
      setLoading(false);
    })();
  }, [user, isLocked]);

  const latestScore = useMemo(() => {
    if (history.length === 0) return null;
    return history[history.length - 1];
  }, [history]);

  const liveScore = useMemo(() => {
    const r = parseInt(revenue) || 0;
    const l = parseInt(laborCost) || 0;
    const f = parseInt(foodCost) || 0;
    const rn = parseInt(rent) || 0;
    if (r === 0) return null;
    return calcScore(r, l, f, rn);
  }, [revenue, laborCost, foodCost, rent]);

  const displayScore = liveScore ?? latestScore?.score ?? 0;
  const sc = scoreColor(displayScore);

  const handleSubmit = async () => {
    if (!user) return;
    const r = parseInt(revenue) || 0;
    const l = parseInt(laborCost) || 0;
    const f = parseInt(foodCost) || 0;
    const rn = parseInt(rent) || 0;
    if (r === 0) { toast.error("Revenue must be greater than 0"); return; }

    setSubmitting(true);
    try {
      // Call AI for recommendations
      const { data: fnData, error: fnError } = await supabase.functions.invoke("survival-score", {
        body: { revenue: r, labor_cost: l, food_cost: f, rent: rn },
      });

      if (fnError) throw fnError;

      const score = fnData.score as number;
      const recs = (fnData.recommendations as string[]) ?? [];
      setRecommendations(recs);

      // Upsert to db
      const { error: dbError } = await supabase
        .from("survival_scores")
        .upsert(
          {
            user_id: user.id,
            month_label: currentMonth,
            revenue: r,
            labor_cost: l,
            food_cost: f,
            rent: rn,
            score,
            recommendations: recs as any,
          },
          { onConflict: "user_id,month_label" }
        );

      if (dbError) throw dbError;

      // Update local history
      setHistory((prev) => {
        const idx = prev.findIndex((h) => h.month_label === currentMonth);
        const entry: ScoreEntry = { month_label: currentMonth, score, revenue: r, labor_cost: l, food_cost: f, rent: rn, recommendations: recs };
        if (idx >= 0) {
          const copy = [...prev];
          copy[idx] = entry;
          return copy;
        }
        return [...prev, entry];
      });

      toast.success("Survival Score updated!");
    } catch (err: any) {
      console.error(err);
      toast.error("Failed to calculate score. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── LOCKED STATE ──
  if (isLocked) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative rounded-2xl border border-border bg-card p-8 mb-8 overflow-hidden"
      >
        {/* Blurred content behind */}
        <div className="absolute inset-0 bg-card/60 backdrop-blur-md z-10" />
        <div className="blur-sm pointer-events-none select-none">
          <div className="flex items-center gap-3 mb-4">
            <Icon className="w-6 h-6 text-primary" />
            <h2 className="font-heading text-xl font-medium text-foreground">{meta.title}</h2>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-7xl font-medium text-emerald-500">82</div>
            <div className="space-y-1">
              <div className="h-3 w-48 bg-muted rounded" />
              <div className="h-3 w-36 bg-muted rounded" />
              <div className="h-3 w-40 bg-muted rounded" />
            </div>
          </div>
        </div>
        {/* Lock overlay */}
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center px-6">
          <Lock className="w-8 h-8 text-muted-foreground mb-3" />
          <h3 className="font-heading font-medium text-foreground text-lg mb-2">
            Upgrade to Pro to track your Survival Score
          </h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-sm">
            Enter your monthly numbers and get an AI-powered health score with actionable recommendations.
          </p>
          <Button className="gap-2" onClick={() => window.location.href = "/billing"}>
            Upgrade now <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </motion.div>
    );
  }

  if (loading) {
    return <SurvivalScoreSkeleton />;
  }

  // ── UNLOCKED ──
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-border bg-card p-6 sm:p-8 mb-8 shadow-card"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-medium text-foreground">{meta.title}</h2>
          <p className="text-xs text-muted-foreground">{meta.description} · {currentMonth}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT: Input form */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-foreground">Enter this month's numbers</h4>
          {[
            { label: "Revenue", value: revenue, set: setRevenue, placeholder: "e.g. 85000" },
            { label: "Labor cost", value: laborCost, set: setLaborCost, placeholder: "e.g. 24000" },
            { label: "Food cost", value: foodCost, set: setFoodCost, placeholder: "e.g. 26000" },
            { label: "Rent", value: rent, set: setRent, placeholder: "e.g. 7500" },
          ].map((f) => (
            <div key={f.label}>
              <label className="text-xs text-muted-foreground">{f.label}</label>
              <Input
                type="number"
                value={f.value}
                onChange={(e) => f.set(e.target.value)}
                placeholder={f.placeholder}
                className="h-9 text-sm"
              />
            </div>
          ))}
          <Button
            onClick={handleSubmit}
            disabled={submitting || !revenue}
            className="w-full gap-2 mt-1"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Calculating...</>
            ) : (
              <><TrendingUp className="w-4 h-4" /> Calculate Score</>
            )}
          </Button>
        </div>

        {/* CENTER: Score display */}
        <div className="flex flex-col items-center justify-center text-center">
          {loading ? (
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          ) : (
            <>
              <div className={`text-8xl font-medium tracking-tight ${sc.text}`}>
                {displayScore}
              </div>
              <div className={`text-sm font-medium mt-1 ${sc.text}`}>{sc.label}</div>
              <div className="text-xs text-muted-foreground mt-2">out of 100</div>

              {/* Mini breakdown */}
              {liveScore !== null && (
                <div className="mt-4 space-y-1 text-xs text-left w-full max-w-[200px]">
                  {[
                    { label: "Labor", pct: ((parseInt(laborCost) || 0) / (parseInt(revenue) || 1)) * 100, bench: 30 },
                    { label: "Food", pct: ((parseInt(foodCost) || 0) / (parseInt(revenue) || 1)) * 100, bench: 32 },
                    { label: "Rent", pct: ((parseInt(rent) || 0) / (parseInt(revenue) || 1)) * 100, bench: 10 },
                  ].map((m) => (
                    <div key={m.label} className="flex items-center justify-between">
                      <span className="text-muted-foreground">{m.label}</span>
                      <span className={m.pct > m.bench ? "text-red-500 font-medium" : "text-emerald-500 font-medium"}>
                        {m.pct.toFixed(1)}% <span className="text-muted-foreground/60">/ {m.bench}%</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* RIGHT: AI Recommendations */}
        <div>
          <h4 className="text-sm font-medium text-foreground flex items-center gap-1.5 mb-3">
            <Lightbulb className="w-4 h-4 text-amber-500" /> AI Recommendations
          </h4>
          {recommendations.length > 0 ? (
            <div className="space-y-3">
              {recommendations.map((rec, i) => (
                <div key={i} className="flex items-start gap-2.5 text-sm">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">
                    {i + 1}
                  </span>
                  <p className="text-muted-foreground leading-relaxed">{rec}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground/60 italic">
              Enter your monthly numbers and hit Calculate to get personalized AI recommendations.
            </p>
          )}
        </div>
      </div>

      {/* Score History Chart */}
      {history.length > 1 && (
        <div className="mt-8 pt-6 border-t border-border">
          <h4 className="text-sm font-medium text-foreground mb-4">Score History</h4>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={history}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis
                  dataKey="month_label"
                  tick={{ fontSize: 11 }}
                  className="fill-muted-foreground"
                />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} className="fill-muted-foreground" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                    fontSize: 12,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={{ r: 4, fill: "hsl(var(--primary))" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </motion.div>
  );
}
